import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Drawer,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Pagination,
  Stack,
} from '@mui/material';
import { Description } from '@mui/icons-material';
import { useEntitlement } from '@/components/domain-engine/useEntitlement';
import { DEFAULT_TENANT_ID } from '@/components/domain-engine/TenantSelector';
import { fetchApi } from '@/api/fetchApi';

/**
 * Account statement drawer — `GET /api/v1/accounts/:id/statement` with
 * opening/closing balances and a running-balance line table (§6.1).
 * Gated on the `ledger` domain entitlement.
 *
 * @see Documentation/Specification/01_Requirements/application-agnostic/ledger/LedgerFunctionsAndProcesses.md §6.1
 * @see sprints/2026-10/Milestone_01_CompleteLedgerImplementation/sprint-07-reporting-period-close/README.md T7.5
 */
interface StatementLine {
  ledgerEntryId: string;
  postedAt: string;
  entryType: 'Debit' | 'Credit';
  amount: number;
  description?: string;
  runningBalance: number;
}

interface StatementPayload {
  openingBalance: number;
  closingBalance: number;
  totalDebit: number;
  totalCredit: number;
  page: number;
  pageSize: number;
  totalCount: number;
  lines: StatementLine[];
}

const PAGE_SIZE = 10;

export const StatementDrawer = ({ accountId }: { accountId: string }) => {
  const { hasEntitlement } = useEntitlement(DEFAULT_TENANT_ID);
  const entitled = hasEntitlement('ledger');
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<StatementPayload>({
    queryKey: ['account-statement', accountId, page],
    queryFn: () =>
      fetchApi(`/accounts/${accountId}/statement?page=${page}&pageSize=${PAGE_SIZE}`),
    enabled: open && entitled && Boolean(accountId),
  });

  if (!entitled) return null;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Description />}
        onClick={() => setOpen(true)}
        data-testid="open-statement"
      >
        Statement
      </Button>
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: { xs: '100%', md: 720 } } }}>
        <Box sx={{ p: 3 }} data-testid="statement-drawer">
          <Typography variant="h6" sx={{ mb: 2 }}>Account Statement</Typography>
          {isLoading && <LinearProgress sx={{ mb: 2 }} />}
          {data && (
            <>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="body2">
                    Opening: <strong>{data.openingBalance.toLocaleString()}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Closing: <strong>{data.closingBalance.toLocaleString()}</strong>
                  </Typography>
                  <Chip size="small" label={`Debit ${data.totalDebit.toLocaleString()}`} color="info" />
                  <Chip size="small" label={`Credit ${data.totalCredit.toLocaleString()}`} color="warning" />
                </Box>
              </Paper>
              <Table size="small" data-testid="statement-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Running Balance</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lines.map((line) => (
                    <TableRow key={line.ledgerEntryId} data-testid="statement-line">
                      <TableCell>{line.postedAt.slice(0, 10)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={line.entryType}
                          color={line.entryType === 'Debit' ? 'info' : 'warning'}
                        />
                      </TableCell>
                      <TableCell align="right">{line.amount.toLocaleString()}</TableCell>
                      <TableCell align="right">{line.runningBalance.toLocaleString()}</TableCell>
                      <TableCell>{line.description ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                  {data.lines.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No movement in range
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Stack sx={{ mt: 2, alignItems: 'center' }}>
                <Pagination
                  count={Math.max(1, Math.ceil(data.totalCount / data.pageSize))}
                  page={page}
                  onChange={(_, next) => setPage(next)}
                  data-testid="statement-pagination"
                />
              </Stack>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};
